import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { SystemUser, Role } from '../../types';
import Input from '../Form/Input';
import Select from '../Form/Select';
import Button from '../Form/Button';

interface UserFormProps {
  user?: SystemUser;
  roles: Role[];
  onSubmit: (data: Partial<SystemUser>) => void;
  onCancel: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, roles, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm<SystemUser>({
    defaultValues: user || {
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      roleId: '',
      isActive: true,
      phone: '',
      department: ''
    }
  });

  useEffect(() => {
    if (user) {
      reset(user);
    }
  }, [user, reset]);

  const onFormSubmit = (data: SystemUser) => {
    onSubmit(data);
  };

  const roleOptions = roles
    .filter(role => role.isActive)
    .map(role => ({
      value: role.id,
      label: `${role.name} - ${role.description}`
    }));

  const departmentOptions = [
    { value: 'IT', label: 'Information Technology' },
    { value: 'Human Resources', label: 'Human Resources' },
    { value: 'Operations', label: 'Operations' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Administration', label: 'Administration' },
    { value: 'Maritime Operations', label: 'Maritime Operations' },
    { value: 'Engineering', label: 'Engineering' },
    { value: 'Safety & Compliance', label: 'Safety & Compliance' }
  ];

  const selectedRoleId = watch('roleId');
  const selectedRole = roles.find(role => role.id === selectedRoleId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {user ? `Edit User: ${user.username || user.email || `${user.firstName} ${user.lastName}`}` : 'Create New User'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6">
          <div className="space-y-6">
            {/* Account Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">
                Account Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Username"
                  {...register('username', { required: 'Username is required' })}
                  error={errors.username?.message}
                  placeholder="e.g., john.doe"
                />
                
                <Input
                  label="Email Address"
                  type="email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  error={errors.email?.message}
                  placeholder="john.doe@petromarine.com"
                />

                <Input
                  label={user ? 'New Password (leave blank to keep current)' : 'Password'}
                  type="password"
                  autoComplete="new-password"
                  {...register('password', {
                    required: user ? false : 'Password is required',
                    validate: (v) => !v || /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(v) ||
                      'Min 8 characters with an uppercase, a lowercase and a number',
                  })}
                  error={errors.password?.message}
                  placeholder="********"
                />

                <Input
                  label="Confirm Password"
                  type="password"
                  autoComplete="new-password"
                  {...register('confirmPassword', {
                    validate: (v) => v === watch('password') || 'Passwords do not match',
                  })}
                  error={errors.confirmPassword?.message}
                  placeholder="********"
                />
              </div>
            </div>

            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="First Name"
                  {...register('firstName', { required: 'First name is required' })}
                  error={errors.firstName?.message}
                  placeholder="John"
                />
                
                <Input
                  label="Last Name"
                  {...register('lastName', { required: 'Last name is required' })}
                  error={errors.lastName?.message}
                  placeholder="Doe"
                />
                
                <Input
                  label="Phone Number"
                  {...register('phone')}
                  error={errors.phone?.message}
                  placeholder="+1-555-0123"
                />
                
                <Select
                  label="Department"
                  {...register('department')}
                  options={departmentOptions}
                  error={errors.department?.message}
                />
              </div>
            </div>

            {/* Role & Permissions */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">
                Role & Permissions
              </h3>
              <div className="space-y-4">
                <Select
                  label="Assigned Role"
                  {...register('roleId', { required: 'Role is required' })}
                  options={roleOptions}
                  error={errors.roleId?.message}
                />
                
                {selectedRole && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Selected Role:</h4>
                    <p className="text-sm font-medium text-gray-900">{selectedRole.name}</p>
                    <p className="text-sm text-gray-600 mt-1">{selectedRole.description}</p>
                    {selectedRole.permissions && selectedRole.permissions.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-700 mb-2">Permissions:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedRole.permissions.slice(0, 8).map((permission, idx) => (
                            <span 
                              key={`${permission}-${idx}`}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-ocean-100 text-ocean-800"
                            >
                              {permission.replace('-', ' ')}
                            </span>
                          ))}
                          {selectedRole.permissions.length > 8 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              +{selectedRole.permissions.length - 8} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="h-4 w-4 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Active User Account
                  </label>
                </div>
              </div>
            </div>

            {/* Security Information */}
            {!user && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> The user will sign in with the email and password entered above.
                </p>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-8 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? 'Saving...' : user ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;